import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { merge } from 'lodash/fp';
import { Restriction } from '@mews-distributor/data-access';
import { DateTimeUtc, Time } from '@mews/framework/datetime';
import { RESERVATION_COUNT_CHANGED } from '../entities/reservations/actionTypes';
import { BOOKING_RESTARTED } from '../global/actionTypes';
import { RESOURCE_ORDER_ADDED } from '../resourceOrder/actionTypes';
import { ROOM_MAX_AVAILABLE_COUNT_CHANGED } from './actionTypes';
import { makeHourlyUtcDates } from './hourlyUtils';
import {
    CategoryAvailabilityMatrix,
    FetchServiceAvailabilitySucceeded,
    ServiceAvailability,
    ServiceAvailabilityData,
    TimeUnitsCategoryAvailability,
} from './types';
import { getServiceAvailabilityHash, makeUtcDates } from './utils';

const dataInitialState: ServiceAvailability['data'] = {};

const metaInitialState: ServiceAvailability['meta'] = {};

interface ReservationCountChangedActionPayload {
    availabilityHash: string;
    categoryId: string;
    countChange: number;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    isDayUse: boolean;
    timezone: string;
    startTime?: Time;
    endTime?: Time;
}

interface ResourceAddedActionPayload {
    availabilityHash: string;
    categoryId: string;
    serviceAvailabilityHash: string;
    spaceTypeCount: number;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    isDayUse: boolean;
    timezone: string;
    startTime?: Time;
    endTime?: Time;
}

interface RoomMaxAvailableCountActionPayload {
    availabilityHash: string;
    availableCount: number;
    categoryId: string;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    isDayUse: boolean;
    timezone: string;
    startTime?: Time;
    endTime?: Time;
}

const transformToCategoryAvailabilities = ({
    availabilities,
    timeUnitStartsUtc,
}: {
    availabilities: number[];
    timeUnitStartsUtc: string[];
}): TimeUnitsCategoryAvailability =>
    pipe(
        availabilities,
        array.reduceWithIndex<number, Record<string, number>>({}, (index, acc, current) => {
            const utcDate = timeUnitStartsUtc[index];
            if (utcDate != null) {
                acc[utcDate] = current;
            }
            return acc;
        })
    );

const transformServiceAvailabilities = ({
    categoryAvailabilities,
    timeUnitStartsUtc,
    serviceId,
    enterpriseId,
}: Pick<
    FetchServiceAvailabilitySucceeded,
    'categoryAvailabilities' | 'serviceId' | 'timeUnitStartsUtc' | 'enterpriseId'
>): ServiceAvailabilityData => {
    return pipe(
        categoryAvailabilities,
        array.reduce<CategoryAvailabilityMatrix, ServiceAvailabilityData>({}, (acc, { availabilities, categoryId }) => {
            acc[getServiceAvailabilityHash({ serviceId, categoryId })] = {
                categoryId,
                enterpriseId,
                timeUnitsAvailability: transformToCategoryAvailabilities({
                    availabilities,
                    timeUnitStartsUtc,
                }),
            };
            return acc;
        })
    );
};

interface SetServiceAvailability extends Omit<FetchServiceAvailabilitySucceeded, 'applicableRestrictions'> {
    applicableRestrictions: Restriction[];
}
export const serviceAvailabilityDataSlice = createSlice({
    name: 'serviceAvailabilityData',
    initialState: dataInitialState,
    reducers: {
        fetchServiceAvailabilitySucceeded: (
            state,
            {
                payload: { categoryAvailabilities, timeUnitStartsUtc, serviceId, enterpriseId },
            }: PayloadAction<SetServiceAvailability>
        ) =>
            merge(
                state,
                transformServiceAvailabilities({
                    categoryAvailabilities,
                    serviceId,
                    enterpriseId,
                    timeUnitStartsUtc,
                })
            ),
    },
    extraReducers: (builder) => {
        builder.addCase(
            RESERVATION_COUNT_CHANGED,
            (
                state,
                {
                    payload: {
                        serviceAvailabilityHash,
                        countChange,
                        startUtc,
                        endUtc,
                        isDayUse,
                        timezone,
                        startTime,
                        endTime,
                    },
                }: PayloadAction<ReservationCountChangedActionPayload, typeof RESERVATION_COUNT_CHANGED>
            ) => {
                updateDateRangeCount(state, {
                    startUtc,
                    endUtc,
                    countChange,
                    serviceAvailabilityHash,
                    isDayUse,
                    timezone,
                    startTime,
                    endTime,
                });
            }
        );

        builder.addCase(
            RESOURCE_ORDER_ADDED,
            (
                state,
                {
                    payload: {
                        serviceAvailabilityHash,
                        spaceTypeCount: countChange,
                        startUtc,
                        endUtc,
                        startTime,
                        endTime,
                        isDayUse,
                        timezone,
                    },
                }: PayloadAction<ResourceAddedActionPayload, typeof RESOURCE_ORDER_ADDED>
            ) => {
                updateDateRangeCount(state, {
                    startUtc,
                    endUtc,
                    countChange,
                    serviceAvailabilityHash,
                    isDayUse,
                    timezone,
                    startTime,
                    endTime,
                });
            }
        );

        builder.addCase(
            ROOM_MAX_AVAILABLE_COUNT_CHANGED,
            (
                state,
                {
                    payload: {
                        serviceAvailabilityHash,
                        availableCount,
                        startUtc,
                        endUtc,
                        isDayUse,
                        timezone,
                        startTime,
                        endTime,
                    },
                }: PayloadAction<RoomMaxAvailableCountActionPayload, typeof ROOM_MAX_AVAILABLE_COUNT_CHANGED>
            ) => {
                /**
                 * We skip the rest when record is empty, because we have two slices - serviceAvailability and availability, but only one of them should be used during a session.
                 * Both slices having data is not a state the app can handle and leads to bugs.
                 * This can be removed when the old availability slice is removed.
                 */
                if (record.isEmpty(state)) {
                    return;
                }
                const isHourlyService = startTime != null && endTime != null;
                const makeDateFunc = isHourlyService ? makeHourlyUtcDates : makeUtcDates;
                const dateTimeUtcCollection = makeDateFunc({
                    startUtc,
                    endUtc,
                    includeEndUtc: isDayUse || isHourlyService,
                    timezone,
                });
                const timeUnits = state[serviceAvailabilityHash].timeUnitsAvailability;

                dateTimeUtcCollection.forEach((dateUtc) => {
                    const previousCount = timeUnits[dateUtc];
                    if (previousCount != null) {
                        timeUnits[dateUtc] = availableCount;
                    }
                });
            }
        );
        builder.addCase(BOOKING_RESTARTED, () => dataInitialState);
    },
});
interface UpdateDateRangeCountOptions {
    countChange: number;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    isDayUse: boolean;
    timezone: string;
    startTime?: Time;
    endTime?: Time;
}

const updateDateRangeCount = (
    state: ServiceAvailability['data'],
    {
        startUtc,
        endUtc,
        countChange,
        serviceAvailabilityHash,
        isDayUse,
        timezone,
        startTime,
        endTime,
    }: UpdateDateRangeCountOptions
) => {
    const isHourlyService = startTime != null && endTime != null;
    /**
     * We skip the rest when record is empty, because we have two slices - serviceAvailability and availability, but only one of them should be used during a session.
     * Both slices having data is not a state the app can handle and leads to bugs.
     * This can be removed when the old availability slice is removed.
     */
    if (record.isEmpty(state)) {
        return;
    }

    const makeDateFunc = isHourlyService ? makeHourlyUtcDates : makeUtcDates;
    const dateTimeUtcCollection = makeDateFunc({
        startUtc,
        endUtc,
        includeEndUtc: isDayUse || isHourlyService,
        timezone,
    });
    const timeUnits = state[serviceAvailabilityHash].timeUnitsAvailability;

    dateTimeUtcCollection.forEach((dateUtc) => {
        const previousCount = timeUnits[dateUtc];
        if (previousCount != null) {
            timeUnits[dateUtc] = previousCount - countChange;
        }
    });
};

const { fetchServiceAvailabilitySucceeded } = serviceAvailabilityDataSlice.actions;

interface FetchServiceAvailabilityMetadata {
    requestId: string;
}

const serviceAvailabilityMetaSlice = createSlice({
    name: 'serviceAvailabilityMeta',
    initialState: metaInitialState,
    reducers: {
        fetchServiceAvailabilityStarted: (
            state,
            { payload: { requestId } }: PayloadAction<FetchServiceAvailabilityMetadata>
        ) => {
            state[requestId] = { isFetching: true, hasFailed: false };
        },
        fetchServiceAvailabilityFailed: (
            state,
            { payload: { requestId } }: PayloadAction<FetchServiceAvailabilityMetadata>
        ) => {
            state[requestId] = { isFetching: false, hasFailed: true };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchServiceAvailabilitySucceeded, (state, { payload: { requestId } }) =>
            record.deleteAt(requestId)(state)
        );
        builder.addCase(BOOKING_RESTARTED, () => metaInitialState);
    },
});

export const serviceAvailabilityReducer = combineReducers({
    data: serviceAvailabilityDataSlice.reducer,
    meta: serviceAvailabilityMetaSlice.reducer,
});

const { fetchServiceAvailabilityStarted, fetchServiceAvailabilityFailed } = serviceAvailabilityMetaSlice.actions;

export { fetchServiceAvailabilityStarted, fetchServiceAvailabilitySucceeded, fetchServiceAvailabilityFailed };
